import React, { useState, useEffect, useRef } from "react";
import { Input, Spin, Modal, Col, Row, Tag } from "antd";
import { message } from "../../components/antdMessageBridge";
import CommonObj from "../../CommonObj";
import Html5QrcodePlugin from "../qrscanner/qrscanner";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import { useAuth } from "../../auth/AuthContext";
import { Html5QrcodeScanType } from "html5-qrcode";
import EmptyRackIcon from "../../assets/img/svg/xemptyrack.svg";
import TagCloseIcon from "../../assets/img/svg/xtagclose.svg?react";
import "./bulkrack-update.css";
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";
import ConfirmationModal from "../../components/confirmationmodal/confirmation-modal";
import regexService from "../../components/commonregex/regexService";
import validationService from "../../components/commonvalidation/validationService";
import { showManualEntryDisabledWarning } from "../../components/commonvalidation/manualEntryWarning";
import useFieldPermissions from "../../hooks/useFieldPermissions";

const BulkRackUpdate = () => {
    const [loading, setLoading] = useState(false);
    const [selectedBids, setSelectedBids] = useState([]);
    const [newRackShelf, setNewRackShelf] = useState("");
    const [form, setForm] = useState({ bid: "" });
    const [activeScanner, setActiveScanner] = useState(null);
    const [decodedResults, setDecodedResults] = useState([]);
    const [isDisabled, setIsDisabled] = useState(false);
    const [regexLoaded, setRegexLoaded] = useState(false);
    const { isReadOnly } = useFieldPermissions("bulkRackUpdate");

    const { roleUserId } = useAuth();
    const { accessToken } = useAuthToken();

    const lastScannedBidRef = useRef(null);
    const lastScannedRackRef = useRef(null);
    const isProcessingBidRef = useRef(false);
    const isProcessingRackRef = useRef(false);
    const pendingBidsRef = useRef(new Set());
    const lastBidKeyTimeRef = useRef(0);
    const clearBidTimerRef = useRef(null);
    const lastRackShelfKeyTimeRef = useRef(0);
    const clearRackShelfTimerRef = useRef(null);

    useEffect(() => {
        return () => {
            clearTimeout(clearBidTimerRef.current);
            clearTimeout(clearRackShelfTimerRef.current);
        };
    }, []);

    useEffect(() => {
        const loadRegexPatterns = async () => {
            try {
                await regexService.fetchRegexData(accessToken);
                await validationService.fetchRegexData(accessToken);
                setRegexLoaded(true);
            } catch (error) {
                console.error("Failed to load regex patterns:", error);
            }
        };

        if (accessToken) {
            loadRegexPatterns();
        }
    }, [accessToken]);

    const validateBidFromAPI = async (bidValue) => {
        const formData = new FormData();
        formData.append("BID", bidValue);
        formData.append("UserId", roleUserId);
        try {
            const response = await fetch(CommonObj.Endpoints.ValidateBulkRack, {
                method: "POST",
                mode: "cors",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "X-CSRF-Token": encryptTimestamp(),
                },
                body: formData,
            });

            if (!response.ok) {
                let apiMessage = `Error ${response.status}`;
                try {
                    const errorData = await response.json();
                    apiMessage = errorData?.message || errorData?.error || apiMessage;
                } catch (parseError) {
                    try {
                        const errorText = await response.text();
                        if (errorText) {
                            apiMessage = errorText;
                        }
                    } catch (textError) {
                        // Keep fallback HTTP status message
                    }
                }
                return { valid: false, message: apiMessage };
            }

            const result = await response.json();
            if (result.message === "") {
                return { valid: true, data: result };
            } else {
                return { valid: false, message: result.message || "BID not found" };
            }
        } catch (err) {
            return {
                valid: false,
                message: err?.message || "Something went wrong!. Please contact your administrator",
            };
        }
    };

    const addBidToList = async (bidValue) => {
        const trimmedBid = bidValue?.trim();
        if (!trimmedBid) return;

        if (selectedBids.includes(trimmedBid)) {
            message.error("BID is already scanned and available in the below list");
            return;
        }

        if (pendingBidsRef.current.has(trimmedBid)) return;

        pendingBidsRef.current.add(trimmedBid);

        try {
            const result = await validateBidFromAPI(trimmedBid);
            if (!result.valid) {
                message.error(result.message);
                return;
            }

            setSelectedBids((prev) => prev.includes(trimmedBid) ? prev : [...prev, trimmedBid]);
        } catch (error) {
            message.error(error?.message || "Something went wrong!. Please contact your administrator");
        } finally {
            pendingBidsRef.current.delete(trimmedBid);
        }
    };

    const onNewScanResult = async (decodedText) => {
        if (activeScanner === "BID") {
            if (decodedText === lastScannedBidRef.current || isProcessingBidRef.current) return;

            isProcessingBidRef.current = true;
            lastScannedBidRef.current = decodedText;
            setForm({ bid: decodedText });
            setActiveScanner(null);

            try {
                await addBidToList(decodedText);
            } finally {
                setForm({ bid: "" });
                isProcessingBidRef.current = false;
                setTimeout(() => { lastScannedBidRef.current = null; }, 2000);
            }
        } else if (activeScanner === "Rack") {
            if (decodedText === lastScannedRackRef.current || isProcessingRackRef.current) return;

            isProcessingRackRef.current = true;
            lastScannedRackRef.current = decodedText;
            setNewRackShelf(decodedText);
            setActiveScanner(null);
            isProcessingRackRef.current = false;
            setTimeout(() => { lastScannedRackRef.current = null; }, 2000);
        }
    };

    const removeBidFromList = (bidToRemove) => {
        setSelectedBids((prev) => prev.filter((bid) => bid !== bidToRemove));
    };

    const cusSearch = async () => {
        const bidValue = form?.bid?.trim() || "";
        if (bidValue.length > 0) {
            // BID validation
            const bidError = validationService.validateBid(form?.bid);
            if (bidError) {
                message.error(bidError);
                return;
            }else if (bidValue.includes(",")) {
                const bidArray = bidValue.split(",").map((bid) => bid.trim()).filter((bid) => bid.length > 0);
                for (const bid of bidArray) {
                    await addBidToList(bid);
                }
            } else {
                await addBidToList(bidValue);
            }
            setForm({ bid: "" });
        }
    };

    const handleFinalSubmit = async () => {
        setIsDisabled(true);
        setTimeout(() => setIsDisabled(false), 3000);

        if (selectedBids.length === 0) {
            message.error("No books selected");
            return;
        }

        if (!newRackShelf || newRackShelf.trim() === "") {
            message.error("Rack Shelf Code cannot be blank");
            return;
        }

        if (regexLoaded) {
            const validation = regexService.validateRackShelf(newRackShelf.trim());
            if (!validation.isValid) {
                return message.error("Input format invalid: No matching pattern found.");
            }
        }

        const formData = new FormData();
        formData.append("BIDs", selectedBids);
        formData.append("RackShelf", newRackShelf);
        formData.append("UserId", roleUserId);

        try {
            setLoading(true);
            const response = await fetch(CommonObj.Endpoints.SubmitBulkRack, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "X-CSRF-Token": encryptTimestamp(),
                },
                body: formData,
            });

            if (!response.ok) throw new Error("Network response was not ok");

            message.success("Rack Shelf is updated for the selected books");
            setSelectedBids([]);
            setNewRackShelf("");
            setForm({ bid: "" });
        } catch (err) {
            message.error("Failed to update rack");
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        if (selectedBids.length > 0 || newRackShelf) {
            ConfirmationModal.showClearConfirmation(() => {
                setSelectedBids([]);
                setNewRackShelf("");
                setForm({ bid: "" });
            });
        }
    };

    if (loading) return <Spin size="large" style={{ margin: "20px auto" }} />;

    return (
        <>
            <Modal
                title={`Scan ${activeScanner || ""}`}
                open={!!activeScanner}
                onCancel={() => setActiveScanner(null)}
                footer={null}
                destroyOnClose
                className="scan-model-container"
                width={1000}
            >
                <div className="scan-modal">
                    <Html5QrcodePlugin
                        fps={30}
                        qrbox={{ width: 1000, height: 400 }}
                        aspectRatio={2.8}
                        qrCodeSuccessCallback={onNewScanResult}
                        supportedScanTypes={[Html5QrcodeScanType.SCAN_TYPE_CAMERA]}
                    />
                </div>
            </Modal>

            <main className="book-page-main-container ready-to-recylce-container">
                <div className="book-page-container">
                    <div className="book-page-title-container no-padding-right">
                        <div className="book-page-title">
                            <span><img src={EmptyRackIcon} alt="Bulk Rack Update" /></span>
                            <h2>Bulk Rack Update</h2>
                        </div>
                    </div>

                    <div className="book-page-form">
                        <div className="book-page-camera-button-container multiple">
                            <Input
                                value={form.bid}
                                className={form.bid ? "has-id" : ""}
                                onChange={(e) => {
                                    setForm({ bid: e.target.value });
                                }}
                                onKeyDown={(e) => {
                                    if (!isReadOnly("bid")) {
                                        return;
                                    }

                                    // Let native copy/paste behave normally (no custom clipboard handling).
                                    const isCopyOrPasteShortcut =
                                        (e.ctrlKey || e.metaKey) && ["c", "v", "C", "V"].includes(e.key);
                                    if (isCopyOrPasteShortcut) {
                                        return;
                                    }

                                    if (e.key === 'Enter' || e.key === 'Tab') {
                                        return;
                                    }

                                    const currentTime = Date.now();
                                    const timeDiff = currentTime - lastBidKeyTimeRef.current;
                                    lastBidKeyTimeRef.current = currentTime;

                                    // A physical scanner sends characters within a few milliseconds of each
                                    // other, so only block keystrokes that arrive slowly (manual typing).
                                    // The very first keystroke (field currently empty) is always let through
                                    // so a scan burst never loses its first character.
                                    const isSlowManualKeystroke = timeDiff > 50 && e.currentTarget.value.length > 0;
                                    if (!isSlowManualKeystroke) {
                                        return;
                                    }

                                    e.preventDefault();
                                    setForm({ bid: '' });

                                    clearTimeout(clearBidTimerRef.current);
                                    clearBidTimerRef.current = setTimeout(() => {
                                        showManualEntryDisabledWarning();
                                    }, 300);
                                }}
                                onPressEnter={(e) => {
                                    if (isReadOnly("bid")) {
                                        e.preventDefault();
                                        return;
                                    }
                                    cusSearch();
                                }}
                                data-track="BulkRack-open-bid-scanner"
                            />
                            {form.bid.length === 0 && (
                                <label>
                                {isReadOnly("bid") ? "Scan " : "Enter / Scan "}Book ID (BID) using “<strong>Camera Icon</strong>”
                                    <span className="form-required">*</span>
                                </label>
                            )}
                            <button type="button" className="book-page-camera-button"
                                data-track="BulkRack-open-bid-scanner"
                                onClick={() => { setDecodedResults([]); setActiveScanner("BID") }}>
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        cx="12"
                                        cy="13"
                                        r="3"
                                        stroke="#1C274C"
                                        strokeWidth="1.5"
                                    />
                                    <path
                                        opacity="0.5"
                                        d="M10 19.9999H14C16.8089 19.9999 18.2134 19.9999 19.2223 19.3258C19.659 19.0339 20.034 18.6589 20.3259 18.2222C21 17.2133 21 15.8088 21 12.9999C21 10.191 20.9999 8.78673 20.3257 7.77783C20.0339 7.34107 19.6589 6.96607 19.2221 6.67423C18.2132 6.00011 16.8088 6.00011 13.9999 6.00011H9.99985C7.19093 6.00011 5.78646 6.00011 4.77757 6.67423C4.34081 6.96607 3.96581 7.34107 3.67397 7.77783C3 8.7865 3 10.1903 3 12.998L3 12.9999C3 15.8088 3 17.2133 3.67412 18.2222C3.96596 18.6589 4.34096 19.0339 4.77772 19.3258C5.78661 19.9999 7.19108 19.9999 10 19.9999Z"
                                        stroke="#1C274C"
                                        strokeWidth="1.5"
                                    />
                                    <path
                                        d="M18 10H17.5"
                                        stroke="#1C274C"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                    />
                                    <path
                                        opacity="0.5"
                                        d="M14.5 3.5H9.5"
                                        stroke="#1C274C"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                    />
                                </svg>
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M8.00098 4C8.00098 3.44772 8.41425 3 8.92405 3H15.0779C15.5877 3 16.001 3.44772 16.001 4C16.001 4.55228 15.5877 5 15.0779 5H8.92405C8.41425 5 8.00098 4.55228 8.00098 4Z"
                                        fill="url(#paint0_linear_273_6917)"
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M9.77875 21H14.2232C17.3442 21 18.9047 21 20.0257 20.2777C20.511 19.965 20.9277 19.5632 21.252 19.0953C22.001 18.0143 22.001 16.5095 22.001 13.4999C22.001 10.4903 22.0008 8.9857 21.2518 7.90473C20.9275 7.43676 20.5109 7.03497 20.0256 6.72228C18.9046 6 17.3441 6 14.223 6H9.77859C6.65756 6 5.09705 6 3.97605 6.72228C3.49077 7.03497 3.0741 7.43676 2.74984 7.90473C2.00098 8.98547 2.00098 10.4896 2.00098 13.4979L2.00098 13.4999C2.00098 16.5095 2.00098 18.0143 2.75 19.0953C3.07426 19.5632 3.49093 19.965 3.97622 20.2777C5.09721 21 6.65773 21 9.77875 21ZM7.83431 13.4999C7.83431 11.2808 9.69979 9.48196 12.001 9.48196C14.3022 9.48196 16.1676 11.2808 16.1676 13.4999C16.1676 15.7189 14.3022 17.5178 12.001 17.5178C9.69979 17.5178 7.83431 15.7189 7.83431 13.4999ZM9.50098 13.4999C9.50098 12.1685 10.6203 11.0891 12.001 11.0891C13.3817 11.0891 14.501 12.1685 14.501 13.4999C14.501 14.8313 13.3817 15.9106 12.001 15.9106C10.6203 15.9106 9.50098 14.8313 9.50098 13.4999ZM18.1121 9.48196C17.6519 9.48196 17.2788 9.84174 17.2788 10.2855C17.2788 10.7294 17.6519 11.0891 18.1121 11.0891H18.6676C19.1279 11.0891 19.501 10.7294 19.501 10.2855C19.501 9.84174 19.1279 9.48196 18.6676 9.48196H18.1121Z"
                                        fill="url(#paint1_linear_273_6917)"
                                    />
                                    <defs>
                                        <linearGradient
                                            id="paint0_linear_273_6917"
                                            x1="22.601"
                                            y1="11.9503"
                                            x2="1.98296"
                                            y2="14.3301"
                                            gradientUnits="userSpaceOnUse"
                                        >
                                            <stop stopColor="#033D9B" />
                                            <stop offset="1" stopColor="#29ACD5" />
                                        </linearGradient>
                                        <linearGradient
                                            id="paint1_linear_273_6917"
                                            x1="22.601"
                                            y1="11.9503"
                                            x2="1.98296"
                                            y2="14.3301"
                                            gradientUnits="userSpaceOnUse"
                                        >
                                            <stop stopColor="#033D9B" />
                                            <stop offset="1" stopColor="#29ACD5" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </button>
                            <button type="button" className="book-page-camera-button-input-2" onClick={cusSearch}>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10ZM11.0303 6.46967C10.7374 6.17678 10.2626 6.17678 9.96967 6.46967L14.0303 10.5303L11.0303 13.5303L5.96967 13.5303L10.0303 10.5303L5.96967 6.46967Z" fill="url(#paint0_linear)" />
                                    <defs>
                                        <linearGradient id="paint0_linear" x1="20.6" y1="9.94" x2="-0.06" y2="12.09" gradientUnits="userSpaceOnUse">
                                            <stop stopColor="#033D9B" /><stop offset="1" stopColor="#29ACD5" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div className="active-page-container">
                        <div className="active-page-body-container">
                            <div className="rounded-table-wrapper without-table">
                                <h5>Total Books Selected ({selectedBids.length})</h5>
                                <Row gutter={[16, 16]}>
                                    {selectedBids.map((bid) => (
                                        <Col span={4} key={bid}>
                                            <Tag closeIcon={<TagCloseIcon />} onClose={() => removeBidFromList(bid)} closable>
                                                {bid}
                                            </Tag>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        </div>
                    </div>

                    <div className="book-page-footer-container">
                        <div className="book-page-action-container">
                            <div className="book-page-form-scan-rack-container">
                                <span className="form-required-rack">*</span>
                                <input
                                    className="book-page-form-scan-rack-button"
                                    placeholder={(isReadOnly("rackShelf") ? "Scan " : "Enter / Scan ")+"New Rack Shelf Barcode"}
                                    value={newRackShelf}
                                    onChange={(e) => {
                                        setNewRackShelf(e.target.value);
                                    }}
                                    onKeyDown={(e) => {
                                        if (!isReadOnly("rackShelf")) {
                                            return;
                                        }

                                        // Let native copy/paste behave normally (no custom clipboard handling).
                                        const isCopyOrPasteShortcut =
                                            (e.ctrlKey || e.metaKey) && ["c", "v", "C", "V"].includes(e.key);
                                        if (isCopyOrPasteShortcut) {
                                            return;
                                        }

                                        if (e.key === 'Enter' || e.key === 'Tab') {
                                            return;
                                        }

                                        const currentTime = Date.now();
                                        const timeDiff = currentTime - lastRackShelfKeyTimeRef.current;
                                        lastRackShelfKeyTimeRef.current = currentTime;

                                        // A physical scanner sends characters within a few milliseconds of each
                                        // other, so only block keystrokes that arrive slowly (manual typing).
                                        // The very first keystroke (field currently empty) is always let through
                                        // so a scan burst never loses its first character.
                                        const isSlowManualKeystroke = timeDiff > 50 && e.currentTarget.value.length > 0;
                                        if (!isSlowManualKeystroke) {
                                            return;
                                        }

                                        e.preventDefault();
                                        setNewRackShelf('');

                                        clearTimeout(clearRackShelfTimerRef.current);
                                        clearRackShelfTimerRef.current = setTimeout(() => {
                                            showManualEntryDisabledWarning();
                                        }, 300);
                                    }}
                                    data-track="BulkRack-open-rack-scanner"
                                />
                                <button className="book-page-form-scan-rack-button-icon"
                                    data-track="BulkRack-open-rack-scanner"
                                    onClick={() => { setDecodedResults([]); setActiveScanner("Rack") }}>
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <circle
                                            cx="12"
                                            cy="13"
                                            r="3"
                                            stroke="#1C274C"
                                            strokeWidth="1.5"
                                        />
                                        <path
                                            opacity="0.5"
                                            d="M10.0001 20H14.0001C16.809 20 18.2135 20 19.2224 19.3259C19.6591 19.0341 20.0341 18.6591 20.326 18.2223C21.0001 17.2134 21.0001 15.8089 21.0001 13C21.0001 10.1911 21 8.78685 20.3258 7.77795C20.034 7.34119 19.659 6.96619 19.2222 6.67436C18.2133 6.00023 16.8089 6.00023 14 6.00023H9.99995C7.19103 6.00023 5.78656 6.00023 4.77767 6.67436C4.34091 6.96619 3.96591 7.34119 3.67407 7.77795C3.0001 8.78663 3.0001 10.1905 3.0001 12.9982L3.0001 13C3.0001 15.8089 3.0001 17.2134 3.67422 18.2223C3.96605 18.6591 4.34106 19.0341 4.77782 19.3259C5.78671 20 7.19117 20 10.0001 20Z"
                                            stroke="#1C274C"
                                            strokeWidth="1.5"
                                        />
                                        <path
                                            d="M18 10H17.5"
                                            stroke="#1C274C"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                        />
                                        <path
                                            opacity="0.5"
                                            d="M14.5 3.5H9.5"
                                            stroke="#1C274C"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M8.00098 4C8.00098 3.44772 8.41425 3 8.92405 3H15.0779C15.5877 3 16.001 3.44772 16.001 4C16.001 4.55228 15.5877 5 15.0779 5H8.92405C8.41425 5 8.00098 4.55228 8.00098 4Z"
                                            fill="url(#paint0_linear_1086_13736)"
                                        />
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M9.77875 21H14.2232C17.3442 21 18.9047 21 20.0257 20.2777C20.511 19.965 20.9277 19.5632 21.252 19.0953C22.001 18.0143 22.001 16.5095 22.001 13.4999C22.001 10.4903 22.0008 8.9857 21.2518 7.90473C20.9275 7.43676 20.5109 7.03497 20.0256 6.72228C18.9046 6 17.3441 6 14.223 6H9.77859C6.65756 6 5.09705 6 3.97605 6.72228C3.49077 7.03497 3.0741 7.43676 2.74984 7.90473C2.00098 8.98547 2.00098 10.4896 2.00098 13.4979L2.00098 13.4999C2.00098 16.5095 2.00098 18.0143 2.75 19.0953C3.07426 19.5632 3.49093 19.965 3.97622 20.2777C5.09721 21 6.65773 21 9.77875 21ZM7.83431 13.4999C7.83431 11.2808 9.69979 9.48196 12.001 9.48196C14.3022 9.48196 16.1676 11.2808 16.1676 13.4999C16.1676 15.7189 14.3022 17.5178 12.001 17.5178C9.69979 17.5178 7.83431 15.7189 7.83431 13.4999ZM9.50098 13.4999C9.50098 12.1685 10.6203 11.0891 12.001 11.0891C13.3817 11.0891 14.501 12.1685 14.501 13.4999C14.501 14.8313 13.3817 15.9106 12.001 15.9106C10.6203 15.9106 9.50098 14.8313 9.50098 13.4999ZM18.1121 9.48196C17.6519 9.48196 17.2788 9.84174 17.2788 10.2855C17.2788 10.7294 17.6519 11.0891 18.1121 11.0891H18.6676C19.1279 11.0891 19.501 10.7294 19.501 10.2855C19.501 9.84174 19.1279 9.48196 18.6676 9.48196H18.1121Z"
                                            fill="url(#paint1_linear_1086_13736)"
                                        />
                                        <defs>
                                            <linearGradient
                                                id="paint0_linear_1086_13736"
                                                x1="22.601"
                                                y1="11.9503"
                                                x2="1.98296"
                                                y2="14.3301"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stopColor="#033D9B" />
                                                <stop offset="1" stopColor="#29ACD5" />
                                            </linearGradient>
                                            <linearGradient
                                                id="paint1_linear_1086_13736"
                                                x1="22.601"
                                                y1="11.9503"
                                                x2="1.98296"
                                                y2="14.3301"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stopColor="#033D9B" />
                                                <stop offset="1" stopColor="#29ACD5" />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                </button>
                            </div>
                            <div className="book-page-buttons-container justify-space-rack">
                                <button className="reject-button" onClick={handleClear}>Clear</button>
                                <button type="button" className="accept-button" disabled={isDisabled} onClick={handleFinalSubmit}>
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default BulkRackUpdate;